import { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';

import { SettingsSectionHeaderIcon } from 'domains/core/components/SettingsHeader';
import SettingsPageLayout from 'domains/core/components/SettingsPageLayout';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import useCreateRole from 'hooks/mutations/useCreateRole';
import useUpdateRole from 'hooks/mutations/useUpdateRole';
import useGetPermissionsList from 'hooks/queries/useGetPermissionsList';
import useGetRoleById from 'hooks/queries/useGetRoleById';
import { useACLPermission } from 'hooks/useACLPermission';
import { useContentOnlyPermission } from 'hooks/useContentOnlyPermission';
import { URLPaths } from 'models/enums';

import PermissionListing from './PermissionListing';
import RoleDetails from './RoleDetails';

import type { SettingsRolesLocationState } from '../settingsRolesLocationState';
import type { Theme } from '@mui/material/styles';
import type { AccessCentreRole } from 'domains/accessCentre/types';

/** Landmark label for tests / assistive tech (page content is permission editing). */
const ROLE_BUILDER_MAIN_ARIA_LABEL = 'permissions';

const CANCEL_BUTTON_LABEL = 'Cancel';
const SAVE_BUTTON_LABEL = 'Save';
const EDIT_BUTTON_LABEL = 'Edit';
const BACK_BUTTON_LABEL = 'Back';
const SAVE_AND_EXIT_BUTTON_LABEL = 'Save & Exit';

const MIN_ROLE_NAME_LENGTH = 3;

/** API `AccessCentrePermission.module` values listed in content-only role builder (Content + Access Centre). */
const CONTENT_ONLY_PERMISSION_MODULES = ['content', 'access management'] as const;

/** With ACL + LD access-control layer (same as main nav), these modules are not available — hide in role builder. */
const ACL_HIDDEN_PERMISSION_MODULES = new Set(['contacts', 'audiences']);

type BuilderId = 'default' | number;

const getIdFromSearch = (search: string): BuilderId | null => {
    const params = new URLSearchParams(search);
    const id = params.get('id');
    if (id == null) return null;
    if (id === 'default') return 'default';
    const num = parseInt(id, 10);
    return Number.isNaN(num) ? null : num;
};

const getModeFromSearch = (search: string): boolean => {
    const params = new URLSearchParams(search);
    return params.get('mode') === 'edit';
};

const roleBuilderPageRootStackSx = {
    width: '100%',
    height: '100%',
    minHeight: 0,
} as const;

/** Narrow viewports: fill space below the page header so overflow-x lives in a viewport-tall layer. */
const roleBuilderScrollLayerOuterSx = {
    position: 'relative',
    width: '100%',
    flex: { xs: 1, md: 'none' },
    minHeight: { xs: (theme: Theme) => `calc(100vh - ${theme.spacing(26)})`, md: 'auto' },
    minWidth: 0,
    alignSelf: 'stretch',
} as const;

const roleBuilderScrollLayerInnerSx = {
    position: { xs: 'absolute', md: 'static' },
    inset: { xs: 0, md: 'unset' },
    width: '100%',
    height: { xs: '100%', md: 'auto' },
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflowX: { xs: 'auto', md: 'visible' },
    overflowY: { xs: 'auto', md: 'visible' },
    WebkitOverflowScrolling: 'touch',
} as const;

const roleBuilderMainRowStackSx = {
    alignItems: 'flex-start',
    flexWrap: 'nowrap',
    width: { md: '100%' },
    flex: { xs: '0 0 auto', md: 'none' },
} as const;

const roleBuilderDetailsColumnSx = {
    flex: { md: '0 0 30%' },
    width: { xs: 300, md: '30%' },
    flexShrink: 0,
    minWidth: 0,
    maxWidth: { xs: 340, md: 'none' },
} as const;

const roleBuilderPermissionsColumnSx = {
    flex: { md: '1 1 0%' },
    width: { xs: 480, md: '70%' },
    flexShrink: 0,
    minWidth: { xs: 420, md: 0 },
    maxWidth: '100%',
} as const;

/** Keep "Save & Exit" on one line in narrow header layouts; min width avoids cramped wrapping. */
const saveAndExitButtonSx = {
    whiteSpace: 'nowrap',
    minWidth: (theme: Theme) => theme.spacing(15),
    px: 2.5,
    textTransform: 'none',
} as const;

const RoleBuilderPage = () => {
    const history = useHistory();
    const location = useLocation<SettingsRolesLocationState>();
    const idFromQuery = useMemo(() => getIdFromSearch(location.search), [location.search]);
    const isEditMode = useMemo(() => getModeFromSearch(location.search), [location.search]);
    const createRoleDraft = location.state?.createRoleDraft;

    const isCreate = idFromQuery === null || idFromQuery === 'default';
    const roleId = typeof idFromQuery === 'number' ? idFromQuery : 0;
    const isView = !isCreate && !isEditMode;
    const isEdit = !isCreate && isEditMode;

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [permissionIds, setPermissionIds] = useState<number[]>([]);
    const [manuallyEnabledPermissionIds, setManuallyEnabledPermissionIds] = useState<Set<number>>(() => new Set());
    const [showEnabledOnly, setShowEnabledOnly] = useState(isView);
    const [systemRoleType, setSystemRoleType] = useState<AccessCentreRole['system_role_type']>(null);
    const isLockedSystemRole = systemRoleType === 'admin' || systemRoleType === 'no_access';

    const { hasContentOnly } = useContentOnlyPermission();
    const { hasAccessControlLayer } = useACLPermission();

    const { data: roleByIdData, isLoading: isRoleByIdLoading } = useGetRoleById(roleId, {
        enabled: !isCreate,
    });

    useEffect(() => {
        if (isView) {
            setShowEnabledOnly(true);
        } else if (isEdit || isCreate) {
            setShowEnabledOnly(false);
        }
    }, [isCreate, isEdit, isView]);

    useEffect(() => {
        if (!isCreate) return;
        setPermissionIds([]);
        setManuallyEnabledPermissionIds(new Set());
        setSystemRoleType(null);
    }, [isCreate]);

    useEffect(() => {
        if (!isCreate) return;
        if (createRoleDraft?.name) {
            setName(createRoleDraft.name);
        }
        if (createRoleDraft?.description) {
            setDescription(createRoleDraft.description);
        }
    }, [isCreate, createRoleDraft]);

    useEffect(() => {
        if (isCreate || !roleByIdData) return;
        const role = roleByIdData?.data?.role ?? roleByIdData?.role;
        if (!role) return;
        setName(role.name ?? '');
        setDescription(role.description ?? '');
        setSystemRoleType(role.system_role_type ?? null);
        const ids = (role.permissions ?? [])
            .filter((perm) => {
                if (!hasAccessControlLayer) return true;
                return !ACL_HIDDEN_PERMISSION_MODULES.has(perm.module);
            })
            .map((p) => p.id);
        setPermissionIds(ids);
        setManuallyEnabledPermissionIds(new Set(ids));
    }, [isCreate, roleByIdData, hasAccessControlLayer]);

    const navigateToRolesWithSuccess = useCallback(
        (rolesListSuccessMessage: string) => {
            history.push(URLPaths.SETTINGS_ROLES, { rolesListSuccessMessage });
        },
        [history]
    );

    const {
        mutate: mutateCreateRole,
        isLoading: isCreateRoleLoading,
        isError: isCreateRoleError,
        error: createRoleError,
    } = useCreateRole({
        onSuccess: (_data, variables) => {
            navigateToRolesWithSuccess(`Role created. ${variables.name} is ready to be assigned to users`);
        },
    });

    const updateRoleMutation = useUpdateRole(roleId, {
        onSuccess: (_data, variables) => {
            navigateToRolesWithSuccess(`Role ${variables.name} updated`);
        },
    });

    const { data: permissionsData, isLoading: isPermissionsLoading } = useGetPermissionsList({
        limit: 100,
        offset: 0,
    });

    const permissions = useMemo(() => permissionsData?.data ?? [], [permissionsData]);

    const permissionsForRoleBuilder = useMemo(
        () =>
            hasAccessControlLayer
                ? permissions.filter((p) => !ACL_HIDDEN_PERMISSION_MODULES.has(p.module))
                : permissions,
        [permissions, hasAccessControlLayer]
    );

    const showRoleNameLengthError = name.trim().length > 0 && name.trim().length < MIN_ROLE_NAME_LENGTH;

    const canSubmit =
        name.trim().length >= MIN_ROLE_NAME_LENGTH &&
        permissionIds.length >= 1 &&
        !isCreateRoleLoading &&
        !updateRoleMutation.isLoading;

    const handleCreateRole = useCallback(() => {
        if (!canSubmit || !isCreate) return;
        mutateCreateRole({
            name: name.trim(),
            description: description.trim() || undefined,
            permissionIds,
        });
    }, [canSubmit, isCreate, name, description, permissionIds, mutateCreateRole]);

    const handleUpdateRole = useCallback(() => {
        if (!canSubmit || !isEdit) return;
        if (isLockedSystemRole) return;
        updateRoleMutation.mutate({
            name: name.trim(),
            description: description.trim() || undefined,
            permissionIds,
        });
    }, [canSubmit, isEdit, isLockedSystemRole, name, description, permissionIds, updateRoleMutation]);

    const goToRolesList = useCallback(() => {
        history.push(URLPaths.SETTINGS_ROLES);
    }, [history]);

    const handleEnterEditMode = useCallback(() => {
        history.push({
            pathname: URLPaths.SETTINGS_ROLES,
            search: `?id=${roleId}&mode=edit`,
        });
    }, [history, roleId]);

    const isPageLoading = !isCreate && isRoleByIdLoading;
    const readOnly = !isCreate && (isView || isLockedSystemRole);

    const saveDisabled = readOnly || !canSubmit;

    const headerAction = (() => {
        if (isCreate) {
            return (
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                        variant="text"
                        onClick={goToRolesList}
                        data-testid="access-center-role-builder-cancel-button"
                    >
                        {CANCEL_BUTTON_LABEL}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateRole}
                        disabled={!canSubmit}
                        data-testid="access-center-role-builder-save-button"
                    >
                        {SAVE_BUTTON_LABEL}
                    </Button>
                </Stack>
            );
        }
        if (!isCreate && isLockedSystemRole) {
            return (
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                        variant="outlined"
                        onClick={goToRolesList}
                        data-testid="access-center-role-builder-back-button"
                    >
                        {BACK_BUTTON_LABEL}
                    </Button>
                </Stack>
            );
        }
        if (isView) {
            return (
                <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                        variant="text"
                        onClick={handleEnterEditMode}
                        data-testid="access-center-role-builder-edit-button"
                    >
                        {EDIT_BUTTON_LABEL}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={goToRolesList}
                        data-testid="access-center-role-builder-back-button"
                    >
                        {BACK_BUTTON_LABEL}
                    </Button>
                </Stack>
            );
        }
        return (
            <Stack direction="row" spacing={2} alignItems="center">
                <Button
                    variant="text"
                    onClick={goToRolesList}
                    data-testid="access-center-role-builder-back-text-button"
                >
                    {BACK_BUTTON_LABEL}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUpdateRole}
                    disabled={saveDisabled}
                    data-testid="access-center-role-builder-save-exit-button"
                    sx={saveAndExitButtonSx}
                >
                    {SAVE_AND_EXIT_BUTTON_LABEL}
                </Button>
            </Stack>
        );
    })();

    const handleManuallyEnabledChange = useCallback((ids: Set<number>) => {
        setManuallyEnabledPermissionIds(new Set(ids));
    }, []);

    return (
        <Stack
            data-testid="access-center-create-role-page"
            role="main"
            aria-label={ROLE_BUILDER_MAIN_ARIA_LABEL}
            sx={roleBuilderPageRootStackSx}
        >
            <SettingsPageLayout
                showSettingsSubheader={false}
                header={
                    <Typography
                        color="primary.main"
                        variant="body1"
                        whiteSpace="nowrap"
                        paddingTop={0.5}
                        sx={{ lineHeight: 1, fontWeight: 400 }}
                    >
                        roles
                    </Typography>
                }
                headerIcon={<SettingsSectionHeaderIcon compact />}
                headerAction={headerAction}
            >
                {/**
                 * Narrow viewports: fill space below the page header so overflow-x lives in a viewport-tall
                 * layer — horizontal scrollbar stays at the bottom of the screen (like Contacts table area),
                 * not directly under the last permission module.
                 */}
                <Box sx={roleBuilderScrollLayerOuterSx}>
                    <Box sx={roleBuilderScrollLayerInnerSx}>
                        <Stack direction="row" spacing={3} sx={roleBuilderMainRowStackSx}>
                            <Box sx={roleBuilderDetailsColumnSx}>
                                <Typography
                                    variant="h4"
                                    color="primary.main"
                                    component="h2"
                                    sx={{ mb: 5, fontWeight: 500, lineHeight: 1 }}
                                    data-testid="access-center-role-builder-permissions-heading"
                                >
                                    permissions
                                </Typography>
                                <RoleDetails
                                    name={name}
                                    description={description}
                                    onNameChange={setName}
                                    onDescriptionChange={setDescription}
                                    showRoleNameLengthError={showRoleNameLengthError}
                                    showEnabledOnly={showEnabledOnly}
                                    onShowEnabledOnlyChange={setShowEnabledOnly}
                                    readOnly={readOnly}
                                    isLoading={isPageLoading}
                                />
                            </Box>
                            <Box sx={roleBuilderPermissionsColumnSx}>
                                <PermissionListing
                                    permissions={permissionsForRoleBuilder}
                                    restrictToPermissionModule={
                                        hasContentOnly ? CONTENT_ONLY_PERMISSION_MODULES : undefined
                                    }
                                    selectedPermissionIds={permissionIds}
                                    manuallyEnabledPermissionIds={manuallyEnabledPermissionIds}
                                    onSelectionChange={setPermissionIds}
                                    onManuallyEnabledChange={handleManuallyEnabledChange}
                                    showEnabledOnly={showEnabledOnly}
                                    isLoading={isPermissionsLoading || isPageLoading}
                                    readOnly={readOnly}
                                />
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </SettingsPageLayout>
            {isCreateRoleError && createRoleError && (
                <ErrorSnackbar
                    key={createRoleError instanceof Error ? createRoleError.message : String(createRoleError)}
                    errorMessage={createRoleError instanceof Error ? createRoleError.message : String(createRoleError)}
                />
            )}
            {updateRoleMutation.isError && updateRoleMutation.error && (
                <ErrorSnackbar
                    key={
                        updateRoleMutation.error instanceof Error
                            ? updateRoleMutation.error.message
                            : String(updateRoleMutation.error)
                    }
                    errorMessage={
                        updateRoleMutation.error instanceof Error
                            ? updateRoleMutation.error.message
                            : String(updateRoleMutation.error)
                    }
                />
            )}
        </Stack>
    );
};

export default RoleBuilderPage;
